import { useIonModal } from '@ionic/react';
import {
  type ComponentType,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

type IonModalPresentOptions = Parameters<
  ReturnType<typeof useIonModal>[0]
>[0];

/**
 * Ionic `useIonModal` only forwards the hook's 2nd argument to the React component.
 * Options passed to `present()` are overlay settings (e.g. cssClass), not component props.
 */
export function useIonModalProps<TProps extends Record<string, unknown>>(
  component: ComponentType<TProps & { dismiss: () => void }>,
  ionOptions?: IonModalPresentOptions
) {
  const [session, setSession] = useState<TProps | null>(null);
  const pendingOpenRef = useRef(false);

  const [presentOverlay, dismissOverlay] = useIonModal(component, {
    ...(session ?? ({} as TProps)),
    dismiss: () => {
      pendingOpenRef.current = false;
      setSession(null);
      void dismissOverlay();
    },
  } as TProps & { dismiss: () => void });

  const open = useCallback((props: TProps) => {
    pendingOpenRef.current = true;
    setSession(props);
  }, []);

  useEffect(() => {
    if (!pendingOpenRef.current || session == null) {
      return;
    }
    pendingOpenRef.current = false;
    void presentOverlay(ionOptions ?? {});
  }, [session, presentOverlay, ionOptions]);

  return { open };
}
